export const column_mapping: Record<string, string> = {
  "user_name": "name",
  "user_email": "email",
  "user_mobilenumber": "mobile",
  "user_gender": "gender",
  "LastActiveTimestamp": "last_active_timestamp",
  "LastActive": "last_active",
  "tags": "tags",
};

export function mapColumn(key: string): string {
  return column_mapping[key] || key;
}

export function mapTagOperand(operand: string): string {
  if (operand === 'not_in') return 'neq';
  if (operand === 'in') return 'eq';
  return operand;
}

export function getOperand(condition: string): string | null {
  if (condition.includes('!=')) return "neq";
  if (condition.includes('==') || condition.includes('=')) return "eq";
  if (condition.includes(' in unnest(')) return "eq";
  if (condition.includes(' not in unnest(')) return "neq";
  if (condition.includes('>')) return "ge";
  if (condition.includes('<')) return "le";
  return null;
}

export function parseSingleCondition(
  condition: string,
  userId?: string | number,
): object | null {
  if (!condition) return null;
  condition = condition.trim();
  let key: string | undefined;
  let value: string | undefined;

  // Handle new DLR status SQL-like condition
  const dlrRegex = /conversation_id in\s*\(\s*select\s*\*\s*from\s*analytics\.tf_Conversation_Broadcast_DLR\((\d+),(\d+),(.+)\)\s*\)/i;
  const dlrMatch = condition.match(dlrRegex);
  if (dlrMatch) {
    const dlrUserId = dlrMatch[1];
    const broadcastId = dlrMatch[2];
    const statusesRaw = dlrMatch[3];
    let statuses: string[];
    try {
      statuses = JSON.parse(statusesRaw);
    } catch {
      const statusMatches = statusesRaw.match(/['"]([^'"]+)['"]/g);
      statuses = statusMatches ? statusMatches.map((s) => s.replace(/^['"]|['"]$/g, '')) : [];
    }
    return {
      conversation_id: {
        operand: "in",
        value: {
          table: "broadcast_dlr",
          args: [dlrUserId, broadcastId, statuses],
        },
      },
    };
  }

  // Handle broadcast interaction status SQL-like condition - Pattern 1
  const broadcastInteractionRegex1 = /conversation_id\s+IN\s*\(\s*SELECT\s*\*\s*FROM\s+analytics\.tf_Conversation_JumperEvent_Template\s*\(\s*([\d,\s]+),\s*\[([^\]]+)\]\s*\)\s*\)/i;
  const broadcastInteractionMatch1 = condition.match(broadcastInteractionRegex1);
  if (broadcastInteractionMatch1) {
    const argsBeforeArray = broadcastInteractionMatch1[1].split(',').map((s) => s.trim()).filter(Boolean);
    const broadcastId = argsBeforeArray[argsBeforeArray.length - 1];
    const resolvedUserId = argsBeforeArray.length > 1 ? argsBeforeArray[0] : userId;

    const buttonIdsRaw = broadcastInteractionMatch1[2];
    const buttonIds = buttonIdsRaw.split(',').map((s) => {
      const trimmed = s.trim();
      return Number.isNaN(Number(trimmed)) ? trimmed.replace(/^['"]|['"]$/g, '') : Number(trimmed);
    }).filter((id) => id !== '' && id !== null && id !== undefined);

    return {
      conversation_id: {
        operand: "in",
        value: {
          table: "jumper_event_template",
          args: [resolvedUserId, broadcastId, buttonIds],
        },
      },
    };
  }

  // Handle marketing opt-in state JSON condition
  if (condition.startsWith('{"key":"marketing_opt_in_state"') || condition.includes('"key":"marketing_opt_in_state"')) {
    try {
      const jsonValue = JSON.parse(condition);
      if (jsonValue.key === 'marketing_opt_in_state') {
        return {
          "marketing_opt_in_state": {
            "operand": jsonValue.operand,
            "value": jsonValue.value,
          },
        };
      }
    } catch (error) {
      console.error('Error parsing marketing opt-in state condition:', error);
      return null;
    }
  }

  // Handle marketing opt-in state SQL IN condition
  const marketingOptinInRegex = /marketing_opt_in_state\s+IN\s*\(([^)]+)\)/i;
  if (marketingOptinInRegex.test(condition)) {
    const match = condition.match(marketingOptinInRegex);
    if (match) {
      const valuesStr = match[1];
      const values = valuesStr.split(',').map((val) => {
        const trimmed = val.trim();
        const cleaned = trimmed.replace(/^['"]|['"]$/g, '');
        return isNaN(Number(cleaned)) ? cleaned : parseInt(cleaned, 10);
      });
      return {
        "marketing_opt_in_state": {
          "operand": "in",
          "value": values,
        },
      };
    }
  }

  // Handle old DLR status JSON condition
  if (condition.includes('conversation_id')) {
    try {
      const jsonStart = condition.indexOf('=') + 1;
      const jsonStr = condition.substring(jsonStart).trim();
      const jsonValue = JSON.parse(jsonStr);

      if (jsonValue.value?.table === 'broadcast_dlr' && Array.isArray(jsonValue.value.args)) {
        if (!jsonValue.value.args.includes(userId)) {
          jsonValue.value.args.unshift(userId);
        }
      }

      return { "conversation_id": jsonValue };
    } catch (error) {
      console.error('Error parsing DLR condition:', error);
      return null;
    }
  }

  // Handle old broadcast interaction status JSON condition
  if (condition.includes('broadcast_interaction_status')) {
    try {
      const jsonStart = condition.indexOf('=') + 1;
      const jsonStr = condition.substring(jsonStart).trim();
      const jsonValue = JSON.parse(jsonStr);

      if (jsonValue.value?.table === 'jumper_event_template' && Array.isArray(jsonValue.value.args)) {
        if (!jsonValue.value.args.includes(userId)) {
          jsonValue.value.args.unshift(userId);
        }
      }

      return { "broadcast_interaction_status": jsonValue };
    } catch (error) {
      console.error('Error parsing broadcast interaction status condition:', error);
      return null;
    }
  }

  if (condition.startsWith('platform=')) {
    value = condition.split('=')[1]?.trim().replace(/"/g, '');
    return { "channel": value };
  }

  if (condition.toLowerCase().includes('not in unnest') || condition.toLowerCase().includes('in unnest')) {
    const operand = condition.toLowerCase().includes('not in unnest') ? 'not_in' : 'in';
    const parts = condition.split(/\s+(not\s+)?in\s+unnest\s*\(/i);

    if (parts.length >= 2) {
      value = parts[0].trim().replace(/^"+|"+$/g, '');
      key = parts[parts.length - 1].replace(/\)\s*$/, '').trim();

      if (key && value) {
        return { [mapColumn(key)]: { "operand": mapTagOperand(operand), "value": value } };
      }
    }
  }

  // Handle other conditions
  if (condition.includes('!=')) {
    [key, value] = condition.split('!=');
    key = key?.trim();
    value = value?.trim().replace(/"/g, '');
  } else if (condition.includes('==')) {
    [key, value] = condition.split('==');
    key = key?.trim();
    value = value?.trim().replace(/"/g, '');
  } else if (condition.includes('=')) {
    [key, value] = condition.split('=');
    key = key?.trim();
    value = value?.trim().replace(/"/g, '');
  } else if (condition.includes('is null')) {
    key = condition.split('is null')[0]?.trim();
    return { [mapColumn(key!)]: { "operand": "is_null", "value": true } };
  } else if (condition.includes('>')) {
    [key, value] = condition.split('>');
    key = key?.trim();
    value = value?.trim().replace(/"/g, '');
  } else if (condition.includes('<')) {
    [key, value] = condition.split('<');
    key = key?.trim();
    value = value?.trim().replace(/"/g, '');
  }

  if (!key || !value) {
    return null;
  }

  return { [mapColumn(key)]: { "operand": getOperand(condition), "value": value } };
}

export function parseTargetingConditions(
  conditionsString: string | undefined | null,
  userId?: string | number,
): object[] {
  const targetingConditions: object[] = [];

  if (!conditionsString) {
    return targetingConditions;
  }

  const conditions = conditionsString.split('&&');

  conditions.forEach((condition) => {
    if (condition) {
      const trimmedCondition = condition.trim();
      if (trimmedCondition.startsWith('(') && trimmedCondition.endsWith(')')) {
        const innerConditions = trimmedCondition.substring(1, trimmedCondition.length - 1).split('||');
        const optionalMatches = innerConditions
          .map((innerCondition) => parseSingleCondition(innerCondition.trim(), userId))
          .filter(Boolean);
        targetingConditions.push({ "optional_matches": optionalMatches });
      } else {
        const parsedCondition = parseSingleCondition(trimmedCondition, userId);
        if (parsedCondition) {
          targetingConditions.push(parsedCondition);
        }
      }
    }
  });

  return targetingConditions;
}
